<!-- 营销活动与配置的展示。数据请求、发券和编辑弹窗由父页面处理。 -->
<template>
  <section class="marketing-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <div class="filter-bar__meta">
          <span>{{ sectionHint }}</span>
        </div>
      </div>

      <div v-if="section === 'coupons'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>券名称</th>
              <th>类型</th>
              <th class="is-right">面额/折扣</th>
              <th class="is-right">库存</th>
              <th class="is-right">已发放</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in coupons" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text">{{ getCouponTypeText(item.type) }}</span></td>
              <td class="is-right">{{ getCouponValueText(item) }}</td>
              <td class="is-right">{{ item.totalCount ?? 0 }}</td>
              <td class="is-right">{{ item.issuedCount ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(couponStatusTone(item.status))]">{{ getCouponStatusText(item.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑优惠券" @click="emit('editCoupon', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="发放优惠券" @click="emit('issueCoupon', item)"><el-icon><Plus /></el-icon></button>
                  <button title="删除优惠券" @click="emit('removeCoupon', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!coupons.length && !loadingCoupons"><td colspan="7"><el-empty description="暂无优惠券数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'banners'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>首页 Banner</th>
              <th>图片</th>
              <th>跳转</th>
              <th class="is-right">排序</th>
              <th>有效期</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in banners" :key="item.id">
              <td><span class="cell-text">{{ item.title }}</span></td>
              <td>
                <img v-if="item.imageUrl" class="banner-thumb" :src="getImageUrl(item.imageUrl)" :alt="item.title">
                <span v-else>--</span>
              </td>
              <td><span class="cell-text">{{ getBannerLinkText(item) }}</span></td>
              <td class="is-right">{{ item.sort ?? 0 }}</td>
              <td>{{ item.startAt || '--' }} 至 {{ item.endAt || '--' }}</td>
              <td><span :class="['status-tag', toneClass(bannerStatusTone(item.enabled))]">{{ getBannerStatusText(item.enabled) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑 Banner" @click="emit('editBanner', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除 Banner" @click="emit('removeBanner', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!banners.length && !loadingBanners"><td colspan="7"><el-empty description="暂无首页 Banner 数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'recharge'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>套餐名称</th>
              <th class="is-right">充值金额</th>
              <th class="is-right">赠送金额</th>
              <th>赠券模板</th>
              <th class="is-right">排序</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in rechargePackages" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td class="is-right">{{ formatAmount(item.amount ?? 0) }}</td>
              <td class="is-right">{{ formatAmount(item.giftBalance ?? 0) }}</td>
              <td><span class="cell-text">{{ item.giftCouponTemplateIds || '--' }}</span></td>
              <td class="is-right">{{ item.sort ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(rechargeStatusTone(item.status))]">{{ getRechargeStatusText(item.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑套餐" @click="emit('editRecharge', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除套餐" @click="emit('removeRecharge', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!rechargePackages.length && !loadingRechargePackages"><td colspan="7"><el-empty description="暂无充值套餐数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'cards'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>模板名称</th>
              <th>类型</th>
              <th class="is-right">售价</th>
              <th class="is-right">面值/次数</th>
              <th class="is-right">已发放</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in cardTemplates" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text">{{ getCardCategoryText(item.category) }}</span></td>
              <td class="is-right">{{ formatAmount(item.salePrice ?? 0) }}</td>
              <td class="is-right">{{ getCardValueText(item) }}</td>
              <td class="is-right">{{ item.issuedCount ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(cardStatusTone(item.status))]">{{ getCardStatusText(item.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑卡券" @click="emit('editCard', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除卡券" @click="emit('removeCard', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!cardTemplates.length && !loadingCardTemplates"><td colspan="7"><el-empty description="暂无卡券模板数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'groupBuys'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>活动名称</th>
              <th>商品</th>
              <th class="is-right">拼团价</th>
              <th class="is-right">成团人数</th>
              <th class="is-right">有效时长</th>
              <th>活动时间</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in groupBuys" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text">{{ item.productName || item.productId || '--' }}</span></td>
              <td class="is-right">{{ formatAmount(item.groupPrice ?? 0) }}</td>
              <td class="is-right">{{ item.teamSize ?? 0 }} 人</td>
              <td class="is-right">{{ item.durationHours ?? 0 }} 小时</td>
              <td>{{ item.startAt || '--' }} 至 {{ item.endAt || '--' }}</td>
              <td><span :class="['status-tag', toneClass(groupBuyStatusTone(item.status))]">{{ getGroupBuyStatusText(item.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑拼团" @click="emit('editGroupBuy', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除拼团" @click="emit('removeGroupBuy', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!groupBuys.length && !loadingGroupBuys"><td colspan="8"><el-empty description="暂无拼团活动数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'bargains'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>活动名称</th>
              <th>商品</th>
              <th class="is-right">初始价</th>
              <th class="is-right">底价</th>
              <th class="is-right">助力次数</th>
              <th>门店</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in bargains" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text">{{ item.productName || item.productId || '--' }}</span></td>
              <td class="is-right">{{ formatAmount(item.initPrice ?? 0) }}</td>
              <td class="is-right">{{ formatAmount(item.floorPrice ?? 0) }}</td>
              <td class="is-right">{{ item.helpTimes ?? 0 }} 次</td>
              <td><span class="cell-text">{{ item.storeId || '--' }}</span></td>
              <td><span :class="['status-tag', toneClass(bargainStatusTone(item.status))]">{{ getBargainStatusText(item.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑砍价" @click="emit('editBargain', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除砍价" @click="emit('removeBargain', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!bargains.length && !loadingBargains"><td colspan="8"><el-empty description="暂无砍价活动数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'invites'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>场景</th>
              <th>邀请人获得的优惠券</th>
              <th>好友获得的优惠券</th>
              <th class="is-right">每人上限</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in inviteConfigs" :key="item.id">
              <td><span class="cell-text">{{ getInviteSceneText(item.scene) }}</span></td>
              <td><span class="cell-text">{{ getCouponName(item.inviterCouponTemplateId) }}</span></td>
              <td><span class="cell-text">{{ getCouponName(item.inviteeCouponTemplateId) }}</span></td>
              <td class="is-right">{{ item.maxTimes ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(inviteStatusTone(item.enabled))]">{{ getInviteStatusText(item.enabled) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑配置" @click="emit('editInvite', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除配置" @click="emit('removeInvite', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!inviteConfigs.length && !loadingInviteConfigs"><td colspan="6"><el-empty description="暂无邀请奖励配置" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>活动名称</th>
              <th>门店</th>
              <th>开始时间</th>
              <th>结束时间</th>
              <th class="is-right">商品数</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in flashSales" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td><span class="cell-text">{{ item.storeNames || item.storeId || '--' }}</span></td>
              <td>{{ item.startAt || '--' }}</td>
              <td>{{ item.endAt || '--' }}</td>
              <td class="is-right">{{ item.items?.length ?? 0 }}</td>
              <td><span :class="['status-tag', toneClass(flashSaleStatusTone(item.status))]">{{ getFlashSaleStatusText(item.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="编辑秒杀" @click="emit('editFlashSale', item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除秒杀" @click="emit('removeFlashSale', item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!flashSales.length && !loadingFlashSales"><td colspan="7"><el-empty description="暂无秒杀活动数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="section === 'coupons' && couponPage" class="table-footer">
        <span>共 {{ couponPage.total }} 条记录，第 {{ couponPage.page }}/{{ couponTotalPages }} 页</span>
        <div>
          <button :disabled="couponPage.page <= 1" @click="emit('changeCouponPage', couponPage.page - 1)">上一页</button>
          <button class="is-active">{{ couponPage.page }}</button>
          <button :disabled="!couponPage.hasMore" @click="emit('changeCouponPage', couponPage.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'
import { Delete, EditPen, Plus } from '@element-plus/icons-vue'
import type { AdminBanner, AdminBargainActivity, AdminCardTemplate, AdminCoupon, AdminFlashSale, AdminGroupBuy, AdminInviteConfig, AdminRechargePackage } from '@/api/marketing'
import type { OperationStat } from '@/data/operationDesign'
import { getImageUrl } from '@/api/upload'

type CouponPage = Awaited<ReturnType<typeof import('@/api/marketing').fetchCoupons>>
type MarketingSection = 'coupons' | 'banners' | 'recharge' | 'cards' | 'groupBuys' | 'bargains' | 'invites' | 'flashSales'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: MarketingSection
  stats: OperationStat[]
  coupons: AdminCoupon[]
  couponOptions: AdminCoupon[]
  couponPage?: CouponPage
  banners: AdminBanner[]
  rechargePackages: AdminRechargePackage[]
  cardTemplates: AdminCardTemplate[]
  groupBuys: AdminGroupBuy[]
  bargains: AdminBargainActivity[]
  inviteConfigs: AdminInviteConfig[]
  flashSales: AdminFlashSale[]
  loadingCoupons: boolean
  loadingBanners: boolean
  loadingRechargePackages: boolean
  loadingCardTemplates: boolean
  loadingGroupBuys: boolean
  loadingBargains: boolean
  loadingInviteConfigs: boolean
  loadingFlashSales: boolean
  couponTotalPages: number
  formatAmount: (amountInFen: number) => string
  getCouponTypeText: (type?: string) => string
  getCouponValueText: (coupon: AdminCoupon) => string
  getCouponStatusText: (status?: string) => string
  getBannerStatusText: (enabled?: boolean) => string
  getBannerLinkText: (banner: AdminBanner) => string
  getRechargeStatusText: (status?: string) => string
  getCardCategoryText: (category?: string) => string
  getCardStatusText: (status?: string) => string
  getCardValueText: (card: AdminCardTemplate) => string
  getGroupBuyStatusText: (status?: string) => string
  getBargainStatusText: (status?: string) => string
  getInviteStatusText: (enabled?: boolean) => string
  getFlashSaleStatusText: (status?: string) => string
  couponStatusTone: (status?: string) => StatusTone
  bannerStatusTone: (enabled?: boolean) => StatusTone
  rechargeStatusTone: (status?: string) => StatusTone
  cardStatusTone: (status?: string) => StatusTone
  groupBuyStatusTone: (status?: string) => StatusTone
  bargainStatusTone: (status?: string) => StatusTone
  inviteStatusTone: (enabled?: boolean) => StatusTone
  flashSaleStatusTone: (status?: string) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  changeCouponPage: [page: number]
  editCoupon: [item: AdminCoupon]
  issueCoupon: [item: AdminCoupon]
  removeCoupon: [item: AdminCoupon]
  editBanner: [item: AdminBanner]
  removeBanner: [item: AdminBanner]
  editRecharge: [item: AdminRechargePackage]
  removeRecharge: [item: AdminRechargePackage]
  editCard: [item: AdminCardTemplate]
  removeCard: [item: AdminCardTemplate]
  editGroupBuy: [item: AdminGroupBuy]
  removeGroupBuy: [item: AdminGroupBuy]
  editBargain: [item: AdminBargainActivity]
  removeBargain: [item: AdminBargainActivity]
  editInvite: [item: AdminInviteConfig]
  removeInvite: [item: AdminInviteConfig]
  editFlashSale: [item: AdminFlashSale]
  removeFlashSale: [item: AdminFlashSale]
}>()

const {
  section,
  stats,
  coupons,
  couponOptions,
  couponPage,
  banners,
  rechargePackages,
  cardTemplates,
  groupBuys,
  bargains,
  inviteConfigs,
  flashSales,
  loadingCoupons,
  loadingBanners,
  loadingRechargePackages,
  loadingCardTemplates,
  loadingGroupBuys,
  loadingBargains,
  loadingInviteConfigs,
  loadingFlashSales,
  couponTotalPages,
  formatAmount,
  getCouponTypeText,
  getCouponValueText,
  getCouponStatusText,
  getBannerStatusText,
  getBannerLinkText,
  getRechargeStatusText,
  getCardCategoryText,
  getCardStatusText,
  getCardValueText,
  getGroupBuyStatusText,
  getBargainStatusText,
  getInviteStatusText,
  getFlashSaleStatusText,
  couponStatusTone,
  bannerStatusTone,
  rechargeStatusTone,
  cardStatusTone,
  groupBuyStatusTone,
  bargainStatusTone,
  inviteStatusTone,
  flashSaleStatusTone,
  toneClass,
} = toRefs(props)

const sectionHint = computed(() => {
  if (section.value === 'coupons') return '当前页已接入真实分页、编辑与发券链路。'
  if (section.value === 'banners') return '首页 Banner 支持真实查询与新增、编辑，启用后会同步到小程序首页。'
  if (section.value === 'recharge') return '充值套餐支持真实查询与新增、编辑。'
  if (section.value === 'cards') return '卡券模板支持真实查询与新增、编辑。'
  if (section.value === 'groupBuys') return '拼团活动支持真实查询与新增、编辑，金额按分保存。'
  if (section.value === 'bargains') return '砍价活动支持真实查询与新增、编辑，金额按分保存。'
  if (section.value === 'invites') return '邀请有礼支持真实查询与新增、编辑，奖励配置会直接同步后端。'
  return '秒杀活动支持真实查询与新增、编辑，活动商品明细随表单一并保存。'
})

const couponNameMap = computed(() => new Map(couponOptions.value.map((coupon) => [coupon.id, coupon.name])))

function getCouponName(couponId?: string): string {
  if (!couponId) return '--'
  return couponNameMap.value.get(couponId) ?? '未找到优惠券'
}

function getInviteSceneText(scene?: string): string {
  if (!scene) return '--'
  if (scene === 'REGISTER') return '新用户注册后'
  return '未识别场景'
}
</script>

<style scoped lang="scss">
.marketing-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-bar__actions { display: flex; gap: 8px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button { height: 36px; padding: 0 16px; border-radius: 8px; background: var(--jf-color-primary); color: #fff; font-size: 14px; font-weight: 700; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.banner-thumb { width: 96px; height: 40px; border-radius: 6px; object-fit: cover; border: 1px solid var(--jf-color-border); vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions button:disabled, .table-footer button:disabled { cursor: not-allowed; opacity: .5; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
